import React, { useId, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import Button from "../../../../shared/components/dashboard-layout/components/button/buttonComponent";
import "./qr-generator-page.scss";

const normalizeUrl = (value) => {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return "";
  }

  try {
    return new URL(trimmedValue).toString();
  } catch (error) {
    try {
      return new URL(`https://${trimmedValue}`).toString();
    } catch (nestedError) {
      return "";
    }
  }
};

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Unable to read the selected logo."));
    reader.readAsDataURL(file);
  });

const getDownloadFileName = (urlValue) => {
  try {
    const { hostname } = new URL(urlValue);
    const sanitizedHost = hostname.replace(/[^a-z0-9-]+/gi, "-").replace(/^-+|-+$/g, "");
    return `relai-qr-${sanitizedHost || "code"}.png`;
  } catch (error) {
    return "relai-qr-code.png";
  }
};

const QrGeneratorPage = () => {
  const [urlInput, setUrlInput] = useState("");
  const [qrValue, setQrValue] = useState("");
  const [logoDataUrl, setLogoDataUrl] = useState("");
  const [logoFileName, setLogoFileName] = useState("");
  const [formError, setFormError] = useState("");
  const [logoError, setLogoError] = useState("");
  const qrCanvasWrapperRef = useRef(null);
  const fileInputRef = useRef(null);
  const logoInputId = useId();

  const handleGenerateQr = (event) => {
    event.preventDefault();

    const normalizedUrl = normalizeUrl(urlInput);

    if (!normalizedUrl) {
      setFormError("Enter a valid URL to generate the QR code.");
      return;
    }

    setFormError("");
    setQrValue(normalizedUrl);
    setUrlInput(normalizedUrl);
  };

  const handleLogoSelection = async (event) => {
    const selectedFile = event.target.files?.[0];

    if (!selectedFile) {
      return;
    }

    if (!selectedFile.type.startsWith("image/")) {
      setLogoError("Choose an image file for the logo.");
      return;
    }

    try {
      const dataUrl = await readFileAsDataUrl(selectedFile);
      setLogoDataUrl(dataUrl);
      setLogoFileName(selectedFile.name);
      setLogoError("");
    } catch (error) {
      setLogoError(error.message);
    }
  };

  const handleClearLogo = () => {
    setLogoDataUrl("");
    setLogoFileName("");
    setLogoError("");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDownloadQr = () => {
    const canvas = qrCanvasWrapperRef.current?.querySelector("canvas");

    if (!canvas || !qrValue) {
      return;
    }

    const downloadLink = document.createElement("a");
    downloadLink.href = canvas.toDataURL("image/png");
    downloadLink.download = getDownloadFileName(qrValue);
    downloadLink.click();
  };

  return (
    <div className="public-qr-generator-page">
      <div className="qr-generator-card">
        <section className="qr-generator-copy">
          <span className="qr-generator-eyebrow">Public Tool</span>
          <h1>QR generator</h1>
          <p className="qr-generator-lead">
            Paste any destination link, generate a QR code, optionally place a logo in the middle,
            and download the final image directly from the browser.
          </p>

          <form className="qr-generator-form" onSubmit={handleGenerateQr}>
            <label className="field-label" htmlFor="qr-url-input">
              Destination URL
            </label>
            <input
              id="qr-url-input"
              className="qr-text-input"
              type="url"
              value={urlInput}
              onChange={(event) => {
                setUrlInput(event.target.value);
                if (formError) {
                  setFormError("");
                }
              }}
              placeholder="example.com/pricing"
            />
            <p className="field-note">If a protocol is missing, `https://` is added automatically.</p>
            {formError ? <p className="field-error">{formError}</p> : null}

            <div className="logo-picker-panel">
              <div className="logo-picker-header">
                <div>
                  <span className="field-label">Optional logo</span>
                  <p className="field-note">
                    Upload it once and keep changing the URL without losing the logo.
                  </p>
                </div>
                {logoDataUrl ? (
                  <button type="button" className="logo-clear-button" onClick={handleClearLogo}>
                    Remove logo
                  </button>
                ) : null}
              </div>

              <div className="logo-picker-body">
                <label className="logo-upload-button" htmlFor={logoInputId}>
                  {logoDataUrl ? "Replace logo" : "Upload logo"}
                </label>
                <input
                  id={logoInputId}
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  onChange={handleLogoSelection}
                />

                <div className={`logo-preview ${logoDataUrl ? "has-logo" : ""}`}>
                  {logoDataUrl ? (
                    <img src={logoDataUrl} alt="Selected QR logo" />
                  ) : (
                    <span>No logo selected</span>
                  )}
                </div>
              </div>

              {logoFileName ? <p className="logo-file-name">{logoFileName}</p> : null}
              {logoError ? <p className="field-error">{logoError}</p> : null}
            </div>

            <div className="qr-generator-actions">
              <Button className="brand" type="submit">
                <span>Generate QR</span>
              </Button>
              <Button className="white" onClick={handleDownloadQr} disabled={!qrValue}>
                <span>Download PNG</span>
              </Button>
            </div>
          </form>
        </section>

        <section className="qr-preview-panel">
          <div className="qr-preview-header">
            <h2>Generated QR</h2>
            <span>{qrValue ? "Ready to scan" : "Waiting for a URL"}</span>
          </div>

          <div className={`qr-preview-stage ${qrValue ? "is-ready" : ""}`} ref={qrCanvasWrapperRef}>
            {qrValue ? (
              <QRCodeCanvas
                value={qrValue}
                size={280}
                includeMargin={true}
                level="H"
                bgColor="#ffffff"
                fgColor="#111111"
                imageSettings={
                  logoDataUrl
                    ? {
                        src: logoDataUrl,
                        height: 56,
                        width: 56,
                        excavate: true,
                      }
                    : undefined
                }
              />
            ) : (
              <div className="qr-empty-state">
                <span className="empty-state-badge">Preview</span>
                <p>Your QR code appears here after you submit a valid URL.</p>
              </div>
            )}
          </div>

          <div className="qr-url-panel">
            <span className="field-label">Connected URL</span>
            {qrValue ? (
              <a href={qrValue} target="_blank" rel="noreferrer">
                {qrValue}
              </a>
            ) : (
              <p>No URL connected yet.</p>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default QrGeneratorPage;
